import { useSyncExternalStore } from 'react';
import { track } from '@/analytics';

/**
 * "Install app" (docs/offline.md): Chromium fires `beforeinstallprompt` once the site is installable (manifest +
 * service worker); we keep that event and the site menu offers Install app, which calls its prompt(). iOS / iPadOS
 * Safari has no install prompt: the menu shows a one-line Add to Home Screen hint instead (Home Screen apps also keep
 * their offline data past Safari's 7-day eviction). Neither shows once the app runs installed (standalone).
 */
interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallOffer = 'prompt' | 'ios-hint' | 'none';

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => { for (const l of listeners) l(); };

export interface InstallPlatform {
    userAgent: string;
    platform: string;
    maxTouchPoints: number;
    standalone: boolean;
}

function currentPlatform(): InstallPlatform | null {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return null;
    const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
        || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
    return { userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints ?? 0, standalone };
}

/** iPhone / iPad Safari (iPadOS reports a Mac platform with touch); not Chrome/Firefox/Edge on iOS (they say so). */
export function isIosSafari(p: InstallPlatform): boolean {
    const ios = /iPad|iPhone|iPod/.test(p.userAgent) || (p.platform === 'MacIntel' && p.maxTouchPoints > 1);
    return ios && /Safari\//.test(p.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(p.userAgent);
}

export function installOffer(hasPrompt: boolean, p: InstallPlatform | null): InstallOffer {
    if (!p || p.standalone || installed) return 'none';
    if (hasPrompt) return 'prompt';
    return isIosSafari(p) ? 'ios-hint' : 'none';
}

let snapshot: InstallOffer = 'none';
const recompute = () => {
    snapshot = installOffer(!!deferred, currentPlatform());
    notify();
};

/** Called once from main.tsx: catch the prompt and the install. */
export function listenForInstallPrompt(win: Window = window): void {
    win.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault(); // our menu entry offers it instead of the browser's mini-infobar
        deferred = e as BeforeInstallPromptEvent;
        recompute();
    });
    win.addEventListener('appinstalled', () => {
        installed = true;
        deferred = null;
        try { track('pwa_installed', {}); } catch { /* analytics never blocks */ }
        recompute();
    });
    recompute();
}

/** The menu's Install app: show the browser's install dialog (the event can be used once). */
export async function promptInstall(): Promise<void> {
    const e = deferred;
    if (!e) return;
    deferred = null;
    recompute();
    try {
        await e.prompt();
        await e.userChoice;
    } catch { /* dismissed or not allowed: nothing to do */ }
}

export function useInstallOffer(): InstallOffer {
    return useSyncExternalStore(
        (l) => { listeners.add(l); return () => listeners.delete(l); },
        () => snapshot,
        () => 'none' as const,
    );
}

/** Tests only. */
export function resetInstallPromptForTests(): void {
    deferred = null;
    installed = false;
    snapshot = 'none';
    notify();
}
